import { Popover } from '@vkontakte/vkui';
import { clsx } from 'clsx';
import { useState, type ReactNode } from 'react';
import { t } from '../../i18n';
import { ChevronDownIcon, TickIcon } from '../icons';
import styles from './ui.module.css';

/**
 * Фильтр списка: чип, который раскрывает варианты списком под собой.
 *
 * Решение владельца 28.09.2026. Пока фильтр был рядом меток, каждая группа
 * занимала свою строку, а длинная (метки склада, роли) переносилась ещё на
 * несколько. Группа-чип занимает одно место, и все группы экрана встают одной
 * строкой.
 *
 * Первый вариант — умолчание списка, он же сброс: обычно «все», у состояния
 * персонажа в админке — «В реестре». Пока выбран он, чип называет ГРУППУ
 * («Роль»), выбрали другое — показывает значение и заливается. Ширину чип
 * берёт по тексту, который показывает, и после выбора может её сменить — это
 * тоже решение владельца: ширину меняет явный выбор в списке, а само состояние
 * меняет только заливку. Длиннее половины строки чип не бывает: метку склада
 * игрок пишет сам, и она обрезается многоточием.
 *
 * Список — вкуишный `Popover`: положение, переворот у нижнего края, закрытие
 * по Esc и нажатию мимо — его. Оправа списка та же, что у `Select`: один и тот
 * же выпадающий список не должен выглядеть двумя способами.
 */

export interface FilterOption<T extends string> {
  value: T;
  label: string;
}

/** Строка фильтров под поиском: все группы экрана одним рядом. */
export function FilterBar({ children }: { children: ReactNode }) {
  return <div className={clsx(styles.filterRow, styles.filterBar)}>{children}</div>;
}

export function FilterSelect<T extends string>({
  label,
  value,
  options,
  onChange,
  disabled,
}: {
  /** Имя группы: его показывает чип, пока выбран первый вариант. */
  label: string;
  value: T;
  options: readonly FilterOption<T>[];
  onChange: (value: T) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [reset] = options;
  if (reset === undefined) return null;

  const current = options.find((option) => option.value === value) ?? reset;
  const active = current.value !== reset.value;

  return (
    <Popover
      trigger="click"
      placement="bottom-start"
      offsetByMainAxis={4}
      noStyling
      shown={open}
      onShownChange={setOpen}
      disabled={disabled}
      content={
        <FilterMenu
          label={label}
          options={options}
          selected={current.value}
          onPick={(next) => {
            setOpen(false);
            if (next !== current.value) onChange(next);
          }}
        />
      }
    >
      <button
        type="button"
        className={clsx(
          styles.filterSelect,
          active && styles.filterSelectActive,
          open && styles.filterSelectOpen,
        )}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('ui.filter.value', { group: label, value: current.label })}
        disabled={disabled}
      >
        <span className={styles.filterSelectText}>{active ? current.label : label}</span>
        <ChevronDownIcon />
      </button>
    </Popover>
  );
}

/**
 * Варианты списком. Роль меню, а не listbox: listbox обещает читалке выбор
 * стрелками, а здесь обычные кнопки, которые обходят Tab.
 */
function FilterMenu<T extends string>({
  label,
  options,
  selected,
  onPick,
}: {
  label: string;
  options: readonly FilterOption<T>[];
  selected: T;
  onPick: (value: T) => void;
}) {
  return (
    <div className={styles.filterMenu} role="menu" aria-label={label}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="menuitemradio"
          aria-checked={option.value === selected}
          className={clsx(
            styles.filterMenuItem,
            option.value === selected && styles.filterMenuItemSelected,
          )}
          onClick={() => onPick(option.value)}
        >
          <span className={styles.filterMenuText}>{option.label}</span>
          {option.value === selected ? <TickIcon /> : null}
        </button>
      ))}
    </div>
  );
}
